import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Form, Formik } from 'formik';
import SelectCheckField from '../../../Components/SelectCheckField/SelectCheckField';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Button from '../../../Components/Button/Button';
import useLogin from '../../Autenticacion/Hooks/useLogin';
import { ObtenerIndicadoresExigenciasPorAreaNegocio } from '../Services/ObtenerIndicadoresExigenciasPorAreaNegocio';
import { ParametrosIngresoEventosConcesionService } from '../Services/ParametrosIngresoEventosConcesionService';
import { IngresoEventosConcesionService } from '../Services/IngresoEventosConcesionService';
import { useToast } from '../../../Hooks/useToast';

const initialFormValues = {
	indicador: '',
	exigencia: '',
};

const FILE_CONFIG = {
	maxFiles: 5,
	maxSizeMB: 5,
	accept: ['.pdf'],
};

const ALLOWED_INDICADORES = ['PAV', 'ILUE', 'SEV', 'DEM', 'CON', 'SOS', 'CIE', 'DOA'];

const SHORT_FIELD_OPTIONS = {
	TipoPavimento: [
		{ value: 1, label: 'Pavimento flexible' },
		{ value: 2, label: 'Pavimento asfáltico' },
		{ value: 3, label: 'Pavimento de concreto' },
	],
	TipoGrieta: [
		{ value: 1, label: 'Parches deteriorados' },
		{ value: 2, label: 'Baches en carpetas asfálticas' },
		{ value: 3, label: 'Baches en tratamiento superficiales' },
	],
	TipoOndulacion: [
		{ value: 1, label: 'Extensivas' },
		{ value: 2, label: 'Individuales' },
	],
	TipoEscalonamiento: [
		{ value: 1, label: 'Junta' },
		{ value: 2, label: 'Grieta' },
	],
	IdViaCaracteristica: [
		{ value: 1, label: 'A — Secciones Rectas' },
		{ value: 2, label: 'B — Zonas de detención' },
		{ value: 3, label: 'C — Curvas Horizontales' },
	],
	IdSubViaCaracteristica: [
		{ value: 1, label: 'A.1 — Secciones rectas con flujo continuo...' },
		{ value: 2, label: 'B.1 — Aproximación a intersecciones...' },
		{ value: 3, label: 'B.2 — Aproximación a condición de detención fija...' },
		{ value: 4, label: 'C.1 — Radio < 250m, sin restricción...' },
		{ value: 5, label: 'C.2 — Radio < 250m, con restricción...' },
		{ value: 6, label: 'C.3 — 250 <= R < 600m' },
	],
	IdCalzada: [
		{ value: 1, label: 'Ascendente' },
		{ value: 2, label: 'Descendente' },
		{ value: 3, label: 'Bidireccional' },
	],
	Entrada: [
		{ value: 1, label: 'Norte' },
		{ value: 2, label: 'Sur' },
	],
	CanalAtencion: [
		{ value: 1, label: 'WEB' },
		{ value: 2, label: 'Presencial' },
		{ value: 3, label: 'Telefónico' },
	],
	Repuesto: [
		{ value: 1, label: 'Sí' },
		{ value: 2, label: 'No' },
	],
	Valor: [
		{ value: 1, label: 'Sí' },
		{ value: 0, label: 'No' },
	],
};

const BOOLEAN_FIELD_OPTIONS = [
	{ value: true, label: 'Cumple' },
	{ value: false, label: 'No cumple' },
];

const YES_NO_FIELD_OPTIONS = [
	{ value: true, label: 'Sí' },
	{ value: false, label: 'No' },
];

const getIntegerMinimum = (fieldName) => fieldName.startsWith('Id') ? 1 : 0;

const isValidInteger = (fieldName, value) => {
	if (value === '' || value === null || value === undefined) return false;

	const parsedValue = Number(value);
	return Number.isInteger(parsedValue) && parsedValue >= getIntegerMinimum(fieldName);
};

const isEmptyValue = (value) => Array.isArray(value)
	? value.length === 0
	: value === '' || value === null || value === undefined;

const ModalNuevoEventoConcesion = ({ show, onHide, onSuccess }) => {
	const formikRef = useRef(null);
	const datosCargadosRef = useRef(false);
	const fileInputRef = useRef(null);
	const [archivos, setArchivos] = useState([]);
	const [indicadorSeleccionado, setIndicadorSeleccionado] = useState(false);
	const [exigenciaSeleccionado, setExigenciaSeleccionado] = useState(false);
	const [indicadorOptions, setIndicadorOptions] = useState([]);
	const [exigenciaOptions, setExigenciaOptions] = useState([]);
	const [indicadorTotal, setIndicadorTotal] = useState([]);
	const [dynamicFields, setDynamicFields] = useState([]);
	const [dynamicValues, setDynamicValues] = useState({});
	const [isLoadingParams, setIsLoadingParams] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fileError, setFileError] = useState('');
	const [fieldErrors, setFieldErrors] = useState({});
	const [exigenciaIdCargada, setExigenciaIdCargada] = useState(null);
	const { usuario } = useLogin();
	const addToast = useToast();

	useEffect(() => {
		if (!show) {
			formikRef.current?.resetForm();
			setArchivos([]);
			setFileError('');
			setIndicadorSeleccionado(false);
			setExigenciaSeleccionado(false);
			setExigenciaIdCargada(null);
			setExigenciaOptions([]);
			setDynamicFields([]);
			setDynamicValues({});
			setFieldErrors({});
			return;
		}

		if (datosCargadosRef.current) return;

		const cargarDatos = async () => {
			const idsArea = usuario?.listaAreaNegocio?.map((x) => x.id) ?? [];
			if (idsArea.length === 0) return;

			const respuestas = await Promise.all(
				idsArea.map((areaNegocioId) =>
					ObtenerIndicadoresExigenciasPorAreaNegocio({
						idUsuario: usuario.id,
						areaNegocioId,
					})
				)
			);

			const indicadores = respuestas
				.filter((respuesta) => respuesta && respuesta.status === 200 && Array.isArray(respuesta.data))
				.flatMap((respuesta) => respuesta.data);

			const indicadoresUnicos = indicadores.reduce((acc, indicador) => {
				const actual = acc.get(indicador.indicadorId);
				if (!actual) {
					acc.set(indicador.indicadorId, {
						...indicador,
						exigencias: indicador.exigencias || [],
					});
					return acc;
				}

				const exigencias = [...(actual.exigencias || []), ...(indicador.exigencias || [])];
				acc.set(indicador.indicadorId, {
					...actual,
					exigencias: [...new Map(exigencias.map((exigencia) => [exigencia.id, exigencia])).values()],
				});
				return acc;
			}, new Map());

			const indicadoresArray = [...indicadoresUnicos.values()];
			const indicadoresFiltrados = indicadoresArray.filter((indicador) =>
				ALLOWED_INDICADORES.some((prefix) => indicador.sigla.startsWith(prefix))
			);
			const options = indicadoresFiltrados.map((indicador) => ({
				value: indicador.indicadorId,
				label: indicador.sigla,
			}));
			setIndicadorOptions(options);
			setIndicadorTotal(indicadoresFiltrados);
			datosCargadosRef.current = true;
		};

		cargarDatos();
	}, [show]);

	useEffect(() => {
		const indicadorValue = formikRef.current?.values?.indicador;
		if (!indicadorValue) {
			setDynamicFields([]);
			setDynamicValues({});
			return;
		}

		const exigenciaValue = formikRef.current?.values?.exigencia;
		if (exigenciaOptions.length > 0 && !exigenciaValue) {
			setDynamicFields([]);
			setDynamicValues({});
			return;
		}

		const fetchParams = async () => {
			setIsLoadingParams(true);
			const resp = await ParametrosIngresoEventosConcesionService({
				IdUsuario: usuario.id,
				IdIndicador: Number(indicadorValue),
				IdExigencia: exigenciaOptions.length > 0 ? Number(exigenciaValue) : null,
			});
			if (resp?.status === 200 && resp.data?.parametros) {
				const sorted = [...resp.data.parametros]
					.filter((p) => p.visible !== false)
					.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
				setDynamicFields(sorted);
				const init = {};
				sorted.forEach((p) => { init[p.nombre] = ''; });
				setDynamicValues(init);
			} else {
				setDynamicFields([]);
				setDynamicValues({});
			}
			setIsLoadingParams(false);
		};

		fetchParams();
	}, [indicadorSeleccionado, exigenciaIdCargada]);

	const limpiarFormulario = () => {
		formikRef.current?.resetForm();
		setArchivos([]);
		setFileError('');
		setIndicadorSeleccionado(false);
		setExigenciaSeleccionado(false);
		setExigenciaIdCargada(null);
		setExigenciaOptions([]);
		setDynamicFields([]);
		setDynamicValues({});
		setFieldErrors({});
	};

	const onIndicadorChange = (name, value) => {
		formikRef.current?.setFieldValue(name, value);
		const isSelected = Boolean(value);
		setIndicadorSeleccionado(isSelected);
		setExigenciaSeleccionado(false);
		setExigenciaIdCargada(null);
		formikRef.current?.setFieldValue('exigencia', '');
		setDynamicFields([]);
		setDynamicValues({});

		if (isSelected) {
			const indicador = indicadorTotal.find((ind) => ind.indicadorId === Number(value));
			const exigencias = (indicador?.exigencias || []).map((exigencia) => ({
				value: exigencia.id,
				label: `${exigencia.sigla} - ${exigencia.descripcion}`,
			}));
			setExigenciaOptions(exigencias);

			if (exigencias.length === 0) {
				setExigenciaSeleccionado(true);
			}
		} else {
			setExigenciaOptions([]);
		}
	};

	const onExigenciaChange = (name, value) => {
		formikRef.current?.setFieldValue(name, value);
		setArchivos([]);
		setFileError('');
		setDynamicFields([]);
		setDynamicValues({});
		setFieldErrors({});
		setExigenciaSeleccionado(Boolean(value));
		setExigenciaIdCargada(value || null);
	};

	const onFileChange = (event) => {
		const files = Array.from(event.target.files || []);
		if (files.length === 0) {
			return;
		}

		const maxSizeBytes = FILE_CONFIG.maxSizeMB * 1024 * 1024;
		const errores = [];

		const validFiles = files.filter((file) => {
			const ext = '.' + file.name.split('.').pop().toLowerCase();
			if (!FILE_CONFIG.accept.includes(ext)) {
				errores.push(`"${file.name}" no es un archivo PDF`);
				return false;
			}
			if (file.size > maxSizeBytes) {
				errores.push(`"${file.name}" supera los ${FILE_CONFIG.maxSizeMB}MB`);
				return false;
			}
			return true;
		});

		if (errores.length > 0) {
			setFileError(errores.join('\n'));
			event.target.value = '';
			return;
		}

		setArchivos((current) => {
			const totalCount = current.length + validFiles.length;
			if (totalCount > FILE_CONFIG.maxFiles) {
				setFileError(`Máximo ${FILE_CONFIG.maxFiles} archivos permitidos`);
				return current;
			}
			const nextFiles = validFiles.filter((file) => !current.some((item) => item.name === file.name));
			setFileError('');
			return [...current, ...nextFiles];
		});

		event.target.value = '';
	};

	const eliminarArchivo = (index) => {
		setArchivos((current) => current.filter((_, currentIndex) => currentIndex !== index));
	};

	const formatFileSize = (file) => {
		const kb = file.size / 1024;
		return `${kb.toFixed(2)} KB`;
	};

	const handleDynamicChange = ({ target: { name, value } }) => {
		setDynamicValues((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: '' }));
	};

	const handleAgregar = async () => {
		const errores = {};
		dynamicFields.forEach((param) => {
			const val = dynamicValues[param.nombre];
			const empty = param.tipoDato === 'array'
				? archivos.length === 0
				: isEmptyValue(val);
			if (param.obligatorio && empty) {
				errores[param.nombre] = 'Campo requerido';
			} else if (param.tipoDato === 'int' && !empty && !isValidInteger(param.nombre, val)) {
				errores[param.nombre] = `Debe ingresar un número entero mayor o igual a ${getIntegerMinimum(param.nombre)}`;
			}
		});

		if (Object.keys(errores).length > 0) {
			setFieldErrors(errores);
			return;
		}

		setFieldErrors({});

		setIsSubmitting(true);
		const evento = {};

		dynamicFields.forEach((param) => {
			const val = dynamicValues[param.nombre];
			if (param.tipoDato === 'array') {
				evento[param.nombre] = archivos.map((archivo) => ({
					NombreArchivo: archivo.name,
					RutaArchivo: '',
				}));
				return;
			}
			let parsed = val;
			if (param.tipoDato === 'float') {
				parsed = Number(val);
			} else if (param.tipoDato === 'int') {
				parsed = Number(val);
			} else if (param.tipoDato === 'bool') {
				parsed = val === true;
			} else if (param.tipoDato === 'short') {
				const shortValue = Array.isArray(val) ? val[0] : val;
				parsed = shortValue === '' || shortValue === null || shortValue === undefined
					? shortValue
					: Number(shortValue);
			}
			evento[param.nombre] = parsed;
		});

		const payload = {
			IdUsuario: usuario.id,
			IdPerfilContexto: usuario.perfilContexto.id,
			IdIndicador: Number(formikRef.current.values.indicador),
			IdExigencia: Number(formikRef.current.values.exigencia),
			ListaEventos: [evento],
		};

		const resp = await IngresoEventosConcesionService(payload, archivos);
		if (resp?.status === 200) {
			addToast({ mostrar: true, mensaje: 'Evento registrado exitosamente', titulo: 'Éxito', tipo: 'Success' });
			onSuccess?.();
			onHide();
			limpiarFormulario();
		} else {
			addToast({ mostrar: true, mensaje: resp?.data?.mensaje || 'Error al registrar evento', titulo: 'Error', tipo: 'Danger' });
		}
		setIsSubmitting(false);
	};

	const renderDynamicField = (param) => {
		const { nombre, etiqueta, obligatorio, tipoDato, modificable } = param;
		const value = dynamicValues[nombre] ?? '';
		const disabled = !modificable;

		if (tipoDato === 'string') {
			return (
				<div key={nombre} className="bitacora-event-modal__field bitacora-event-modal__field--helper">
					<label htmlFor={`nuevo-evento-${nombre}`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
					<input
						id={`nuevo-evento-${nombre}`}
						name={nombre}
						type="text"
						placeholder="Ingresar"
						value={value}
						onChange={handleDynamicChange}
						disabled={disabled}
						required={obligatorio}
					/>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'float') {
			return (
				<div key={nombre} className="bitacora-event-modal__field">
					<label htmlFor={`nuevo-evento-${nombre}`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
					<input
						id={`nuevo-evento-${nombre}`}
						name={nombre}
						type="number"
						step="0.01"
						min="0"
						value={value}
						onChange={handleDynamicChange}
						disabled={disabled}
						required={obligatorio}
					/>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'int') {
			const minimum = getIntegerMinimum(nombre);

			return (
				<div key={nombre} className="bitacora-event-modal__field">
					<label htmlFor={`nuevo-evento-${nombre}`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
					<input
						id={`nuevo-evento-${nombre}`}
						name={nombre}
						type="number"
						step="1"
						min={minimum}
						placeholder="Ingresar"
						value={value}
						onChange={handleDynamicChange}
						onBlur={() => {
							if (value !== '' && !isValidInteger(nombre, value)) {
								setFieldErrors((prev) => ({
									...prev,
									[nombre]: `Debe ingresar un número entero mayor o igual a ${minimum}`,
								}));
							}
						}}
						disabled={disabled}
						required={obligatorio}
					/>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'bool') {
			const fieldOptions = nombre === 'Valor' || etiqueta === 'Cumple'
				? YES_NO_FIELD_OPTIONS
				: BOOLEAN_FIELD_OPTIONS;
			const selectedOption = fieldOptions.find((option) => option.value === value) || null;

			return (
				<div key={nombre} className="bitacora-event-modal__field bitacora-event-modal__field--select">
					<div className="simple-select">
						<label htmlFor={`field-${nombre}`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
						<Select
							inputId={`field-${nombre}`}
							name={nombre}
							classNamePrefix="selector"
							value={selectedOption}
							options={fieldOptions}
							onChange={(option) => handleDynamicChange({
								target: { name: nombre, value: option?.value ?? '' },
							})}
							isDisabled={disabled}
							isSearchable={false}
							placeholder="Seleccionar..."
							noOptionsMessage={() => 'Sin opciones'}
							isClearable
						/>
					</div>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'DateTime') {
			const datePart = value?.split('T')?.[0] || '';
			const timePart = value?.split('T')?.[1] || '';
			return (
				<div key={nombre} className="bitacora-event-modal__row">
					<div className="bitacora-event-modal__field">
						<label htmlFor={`nuevo-evento-${nombre}-date`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
						<input
							id={`nuevo-evento-${nombre}-date`}
							type="date"
							value={datePart}
							onChange={(e) => {
								const newValue = e.target.value + 'T' + timePart;
								handleDynamicChange({ target: { name: nombre, value: newValue } });
							}}
							disabled={disabled}
							required={obligatorio}
						/>
					</div>
					<div className="bitacora-event-modal__field">
						<label htmlFor={`nuevo-evento-${nombre}-time`}>Hora</label>
						<input
							id={`nuevo-evento-${nombre}-time`}
							type="time"
							value={timePart}
							onChange={(e) => {
								const newValue = datePart + 'T' + e.target.value;
								handleDynamicChange({ target: { name: nombre, value: newValue } });
							}}
							disabled={disabled}
						/>
					</div>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'short') {
			const fieldOptions = SHORT_FIELD_OPTIONS[nombre] || null;
			const isSingleSelect = !!fieldOptions;
			const options = fieldOptions || [];
			const selectValue = isSingleSelect
				? fieldOptions.find((o) => o.value === value) || null
				: (Array.isArray(value) ? value.map((v) => ({ value: v, label: v })) : []);
			return (
				<div key={nombre} className="form-floating">
					<div className="simple-select">
						<label htmlFor={`field-${nombre}`}>{etiqueta}{obligatorio && <span className="text-danger"> *</span>}</label>
						<CreatableSelect
							inputId={`field-${nombre}`}
							name={nombre}
							classNamePrefix="selector"
							value={selectValue}
							options={options}
							onChange={(option) => {
								const newValue = isSingleSelect
									? (option ? option.value : null)
									: (Array.isArray(option) ? option.map((o) => o.value) : []);
								handleDynamicChange({ target: { name: nombre, value: newValue } });
							}}
							isDisabled={disabled}
							placeholder={isSingleSelect ? 'Seleccionar...' : 'Escribir o seleccionar...'}
							noOptionsMessage={() => 'Sin opciones'}
							formatCreateLabel={(inputValue) => `Agregar "${inputValue}"`}
							isValidNewOption={isSingleSelect ? () => false : undefined}
							isMulti={!isSingleSelect}
							isClearable
						/>
					</div>
					{fieldErrors[nombre] &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fieldErrors[nombre]}
						</small>
					}
				</div>
			);
		}

		if (tipoDato === 'array') {
			return (
				<div key={nombre}>
					<div className="bitacora-event-modal__upload" onClick={() => fileInputRef.current?.click()}>
						<span>Subir archivo</span>
						<svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
							<path d="M12 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							<path d="M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							<path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
						</svg>
						<input
							ref={fileInputRef}
							type="file"
							multiple
							onChange={onFileChange}
							style={{ display: 'none' }}
						/>
					</div>
					{fileError &&
						<small className="message" style={{ color: '#dc3545', fontSize: '12px', width: '100%' }}>
							{fileError}
						</small>
					}
					{archivos.length > 0 &&
						<div className="bitacora-event-modal__files">
							{archivos.map((file, index) => (
								<div className="bitacora-event-modal__file" key={`${file.name}-${index}`}>
									<i className="ico-doc-empty"></i>
									<div>
										<strong>{file.name}</strong>
										<span>{formatFileSize(file)}</span>
									</div>
									<button type="button" onClick={() => eliminarArchivo(index)} aria-label={`Eliminar ${file.name}`}>
										<i className="ico-close"></i>
									</button>
								</div>
							))}
						</div>
					}
				</div>
			);
		}

		return null;
	};

	const requiredFieldsComplete = useMemo(() => {
		if (dynamicFields.length === 0) return false;
		return dynamicFields.every((param) => {
			if (!param.obligatorio) return true;
			if (param.tipoDato === 'array') return archivos.length > 0;
			const val = dynamicValues[param.nombre];
			if (param.tipoDato === 'int') return isValidInteger(param.nombre, val);
			if (param.tipoDato === 'bool') return typeof val === 'boolean';
			return !isEmptyValue(val);
		});
	}, [dynamicFields, dynamicValues, archivos]);

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			dialogClassName="bitacora-event-modal"
			contentClassName="bitacora-event-modal__content"
		>
			<div className="bitacora-event-modal__shell">
				<div className="bitacora-event-modal__header">
					<h5>Agregar nuevo evento</h5>
					<button type="button" className="bitacora-event-modal__close" onClick={onHide} aria-label="Cerrar">
						<i className="ico-close"></i>
					</button>
				</div>
				<Formik
					innerRef={formikRef}
					initialValues={initialFormValues}
					onSubmit={() => { }}
				>
					{({ setFieldTouched }) => (
						<Form className="bitacora-event-modal__form">
							<SelectCheckField
								name="indicador"
								label="Indicador"
								options={indicadorOptions}
								onChange={onIndicadorChange}
								onBlur={setFieldTouched}
								isMulti={false}
								allowSelectAll={false}
							/>
							{indicadorSeleccionado &&
								<>
									{exigenciaOptions.length > 0 &&
										<SelectCheckField
											name="exigencia"
											label="Exigencia"
											options={exigenciaOptions}
											onChange={onExigenciaChange}
											onBlur={setFieldTouched}
											isMulti={false}
											allowSelectAll={false}
										/>
									}
									{isLoadingParams ? (
										<p className="text-muted mt-2 mb-2">Cargando parametros...</p>
									) : dynamicFields.length > 0 ? (
										dynamicFields.map((param) => renderDynamicField(param))
									) : null}
									<div className="bitacora-event-modal__actions">
										<Button type="button" design="btn--outline btn--pewter-blue bitacora-event-modal__action" onClick={limpiarFormulario}>
											Limpiar
										</Button>
									<Button type="button" design="btn-primary bitacora-event-modal__action" disabled={!requiredFieldsComplete || isSubmitting} onClick={handleAgregar}>
														{isSubmitting ? 'Enviando...' : 'Agregar'}
													</Button>
												</div>
											</>
							}
							{!indicadorSeleccionado &&
								<div className="bitacora-event-modal__actions">
									<Button type="button" design="btn--outline btn--pewter-blue bitacora-event-modal__action" onClick={limpiarFormulario}>
										Limpiar
									</Button>
									<Button type="button" design="btn-primary bitacora-event-modal__action" disabled={!requiredFieldsComplete || isSubmitting} onClick={handleAgregar}>
										{isSubmitting ? 'Enviando...' : 'Agregar'}
									</Button>
								</div>
							}
						</Form>
					)}
				</Formik>
			</div>
		</Modal>
	);
};

export default ModalNuevoEventoConcesion;
